import Phaser from 'phaser';
import {Room} from './Room';
import {CardType, StatType} from "./CardTypes";
import {uiBorderColor} from "./Colors";

export class Map {
    private readonly scene: Phaser.Scene;
    private readonly container: Phaser.GameObjects.Container;
    private readonly rooms: Room[];
    private readonly currentRoomIndex: number;
    private readonly mapConfig = {
        width: 20,
        height: 10,
        padding: 5,
        lineWidth: 2,
        maxRoomsPerRow: 8,
        ySpacing: 4,
        connectionSize: 10,
    };

    constructor(scene: Phaser.Scene, rooms: Room[], currentRoomIndex: number) {
        this.scene = scene;
        this.rooms = rooms;
        this.currentRoomIndex = currentRoomIndex;
        this.container = scene.add.container(0, 0);
        this.container.setScrollFactor(0);
        this.container.setDepth(1000);
        this.createMap();
    }

    private createMap(): void {
        const { width, height } = this.scene.scale;
        const totalRows = Math.ceil(this.rooms.length / this.mapConfig.maxRoomsPerRow);
        const currentRow = Math.floor(this.currentRoomIndex / this.mapConfig.maxRoomsPerRow);

        let startRow = 0;
        if (currentRow >= 3) {
            startRow = currentRow - 2;
        }
        const endRow = Math.min(startRow + 4, totalRows);

        this.container.setPosition(
            100,
            height - 55
        );

        this.rooms.forEach((room, index) => {
            const row = Math.floor(index / this.mapConfig.maxRoomsPerRow);

            if (row < startRow || row >= endRow) {
                return;
            }

            if (index < this.rooms.length - 1) {
                const nextIndex = index + 1;
                const nextRow = Math.floor(nextIndex / this.mapConfig.maxRoomsPerRow);
                const col = index % this.mapConfig.maxRoomsPerRow;
                const x = col * (this.mapConfig.width + this.mapConfig.padding);
                const y = (row - startRow) * (this.mapConfig.height + this.mapConfig.ySpacing);

                const connectionLine = this.scene.add.graphics();
                connectionLine.lineStyle(this.mapConfig.lineWidth, uiBorderColor);

                const isNewRow = row !== nextRow;
                const isNextRoomVisible = nextRow < endRow;

                if (isNewRow) {
                    connectionLine.beginPath();
                    connectionLine.moveTo(
                        x + this.mapConfig.width,
                        y + this.mapConfig.height / 2
                    );
                    connectionLine.lineTo(
                        x + this.mapConfig.width + this.mapConfig.connectionSize,
                        y + this.mapConfig.height / 2
                    );
                    connectionLine.strokePath();

                    if (isNextRoomVisible) {
                        const nextY = (nextRow - startRow) * (this.mapConfig.height + this.mapConfig.ySpacing);
                        const nextConnectionLine = this.scene.add.graphics();
                        nextConnectionLine.lineStyle(this.mapConfig.lineWidth, uiBorderColor);
                        nextConnectionLine.beginPath();
                        nextConnectionLine.moveTo(
                            -this.mapConfig.connectionSize,
                            nextY + this.mapConfig.height / 2
                        );
                        nextConnectionLine.lineTo(
                            0,
                            nextY + this.mapConfig.height / 2
                        );
                        nextConnectionLine.strokePath();
                        this.container.add(nextConnectionLine);
                    }
                } else {
                    connectionLine.beginPath();
                    connectionLine.moveTo(
                        x + this.mapConfig.width,
                        y + this.mapConfig.height / 2
                    );
                    connectionLine.lineTo(
                        x + this.mapConfig.width + this.mapConfig.padding,
                        y + this.mapConfig.height / 2
                    );
                    connectionLine.strokePath();
                }

                this.container.add(connectionLine);
            }
        });

        this.rooms.forEach((room, index) => {
            const row = Math.floor(index / this.mapConfig.maxRoomsPerRow);

            if (row < startRow || row >= endRow) {
                return;
            }

            const col = index % this.mapConfig.maxRoomsPerRow;
            const x = col * (this.mapConfig.width + this.mapConfig.padding);
            const y = (row - startRow) * (this.mapConfig.height + this.mapConfig.ySpacing);

            const roomRect = this.scene.add.graphics();

            let fillColor: number;
            switch (room.getConfig().type) {
                case 1:
                    const cardConfig = room.getConfig().cardConfig
                    if (cardConfig === undefined) {
                        fillColor = 0x444444;
                    }
                    else {
                        if (cardConfig.type === CardType.INCREASE_STAT) {
                            if (cardConfig.statType === StatType.SANCTUARY) {
                                fillColor = 0x00aa00;
                            }
                            else if (cardConfig.statType === StatType.FORGE) {
                                fillColor = 0x0088aa;
                            }
                            else {
                                throw new Error(`Unknown stat type: ${cardConfig.statType}`)
                            }
                        }
                        else if (cardConfig.type === CardType.CREATE_MONSTER) {
                            fillColor = 0xcc0000;
                        }
                        else {
                            throw new Error(`Unknown card type: ${cardConfig.type}`)
                        }
                    }
                    break;
                case 2:
                    fillColor = 0xff0000;
                    break;
                default:
                    fillColor = 0x222222;
            }

            roomRect.fillStyle(fillColor);
            roomRect.fillRect(x, y, this.mapConfig.width, this.mapConfig.height);

            const outlineColor = index === this.currentRoomIndex ? 0x00ff00 : uiBorderColor;
            roomRect.lineStyle(this.mapConfig.lineWidth, outlineColor);
            roomRect.strokeRect(x, y, this.mapConfig.width, this.mapConfig.height);

            this.container.add(roomRect);
        });
    }

    public destroy(): void {
        this.container.destroy();
    }
}
